import { ExternalLink, Mail, Phone } from "lucide-react";
import { CanAccess, useDataProvider, useGetList, useNotify } from "ra-core";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { ClientPhotos } from "./ClientPhotos";
import { ClientRequirements } from "./ClientRequirements";
import { AddressAlertBox } from "./AddressAlertBox";
import { ClientStageBar } from "./ClientStageBar";
import { ClientStripe } from "./ClientStripe";
import { ClientTasks } from "./ClientTasks";
import { ClientWelcome } from "./welcome/ClientWelcome";
import { ClientNotesSection } from "./notes/ClientNotesSection";
import { ClientConversations } from "./social/ClientConversations";
import {
  CALL_SLOTS,
  LEAD_TEMPERATURES,
  LOST_REASONS,
  ORDER_STAGES,
  PAYMENT_METHODS,
  PLANS,
  PRODUCTS,
  formatEuro,
  labelOf,
} from "./gulaConstants";
import {
  fullName,
  type GulaClient,
  type GulaInstaller,
  type GulaOrder,
} from "./gulaTypes";
import { Field, SimpleSelect } from "./SimpleSelect";
import { agentName, useAgents } from "./useAgents";
import { WhatsappCallButton } from "./mobile/WhatsappCallButton";

const EDITABLE_FIELDS = [
  "sales_id",
  "lead_temperature",
  "recontact_at",
  "call_slot",
  "address",
  "postal_code",
  "city",
  "province",
  "installer_id",
  "lost_reason",
] as const;

type Draft = Partial<Record<(typeof EDITABLE_FIELDS)[number], string | null>>;

const toDraft = (client: GulaClient): Draft =>
  Object.fromEntries(
    EDITABLE_FIELDS.map((field) => [
      field,
      client[field] == null ? "" : String(client[field]),
    ]),
  );

/** Phone, email and zone of a client, under its name. */
export const ClientContactLines = ({ client }: { client: GulaClient }) => (
  <div className="flex flex-col gap-1 text-sm text-muted-foreground">
    {/* On the phone: call through WhatsApp Business (the 614) */}
    <span className="md:hidden">
      <WhatsappCallButton
        clientId={client.id}
        name={fullName(client)}
        phone={client.phone}
        variant="pill"
        className="my-1"
      />
    </span>
    {client.phone ? (
      <a href={`tel:${client.phone}`} className="flex items-center gap-2">
        <Phone className="h-4 w-4" />
        {client.phone}
      </a>
    ) : null}
    {client.email ? (
      <a
        href={`mailto:${client.email}`}
        className="flex min-w-0 items-center gap-2"
      >
        <Mail className="h-4 w-4 shrink-0" />
        <span className="truncate">{client.email}</span>
      </a>
    ) : null}
    {client.zone ? (
      <span>
        Zona: <strong>{client.zone}</strong>
        {client.installer_name ? ` · Instala ${client.installer_name}` : ""}
      </span>
    ) : null}
  </div>
);

/** Side panel with everything about one lead/customer, editable in place. */
export const ClientSheet = ({
  client,
  onClose,
  onSaved,
  showStageBar,
}: {
  client: GulaClient | null;
  onClose: () => void;
  onSaved: () => void;
  /** Buttons to close the call (move the lead to its next phase) */
  showStageBar?: boolean;
}) => {
  if (!client) return null;
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{fullName(client)}</SheetTitle>
          <SheetDescription asChild>
            <ClientContactLines client={client} />
          </SheetDescription>
        </SheetHeader>
        {showStageBar ? (
          <div className="px-4">
            <ClientStageBar client={client} onChanged={onSaved} />
          </div>
        ) : null}
        <ClientPanelBody client={client} onSaved={onSaved} />
      </SheetContent>
    </Sheet>
  );
};

/**
 * Everything in the client panel under its name: form answers, notes,
 * requirements, editable fields, WhatsApp, tasks, photos, orders and Stripe.
 * Used by the sheet in Clientes and by the side panel in Mi jornada.
 */
export const ClientPanelBody = ({
  client,
  onSaved,
  className,
}: {
  client: GulaClient;
  onSaved: () => void;
  className?: string;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { workers } = useAgents();
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);

  const { data: installers = [] } = useGetList<GulaInstaller>(
    "gula_installers",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const { data: orders = [] } = useGetList<GulaOrder>("gula_orders", {
    filter: { client_id: client?.id },
    pagination: { page: 1, perPage: 20 },
    sort: { field: "created_at", order: "DESC" },
  });

  // Reloads (e.g. after ticking a requirement) refresh the form only when it has
  // no unsaved edits, or when another client is opened
  const baseline = useRef<{ id?: unknown; draft: Draft }>({ draft: {} });
  useEffect(() => {
    if (!client) return;
    const next = toDraft(client);
    const previous = baseline.current;
    setDraft((current) =>
      previous.id !== client.id ||
      JSON.stringify(current) === JSON.stringify(previous.draft)
        ? next
        : current,
    );
    baseline.current = { id: client.id, draft: next };
  }, [client]);

  const set = (field: keyof Draft) => (value: string) =>
    setDraft((previous) => ({ ...previous, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    const data = Object.fromEntries(
      Object.entries(draft).map(([key, value]) => [
        key,
        value === "" ? null : value,
      ]),
    );
    // Changing the postal code recomputes the zone (and installer) in the database
    if (data.postal_code !== (client.postal_code ?? null)) {
      data.zone = null;
      if (
        String(data.installer_id ?? "") === String(client.installer_id ?? "")
      ) {
        data.installer_id = null;
      }
    }
    try {
      await dataProvider.update("gula_clients", {
        id: client.id,
        data,
        previousData: { id: client.id },
      });
      notify("Cambios guardados", { type: "success" });
      onSaved();
    } catch (error) {
      console.error(error);
      notify("No se han podido guardar los cambios", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const installerOptions = installers.map((installer) => ({
    value: String(installer.id),
    label: `${installer.name} (${installer.zone_label})`,
  }));

  return (
    <div className={cn("flex flex-col gap-5 px-4 pb-6", className)}>
      {client.address_alert ? (
        <AddressAlertBox
          clientId={client.id}
          current={client}
          alert={client.address_alert}
          onResolved={onSaved}
        />
      ) : null}
      {client.lead_answer || client.campaign_name || client.form_name ? (
        <section className="rounded-md bg-muted/50 p-3 text-sm">
          {client.lead_answer ? (
            <p className="whitespace-pre-line">
              <strong>Respuestas del formulario</strong>
              {"\n"}
              {client.lead_answer}
            </p>
          ) : null}
          {client.campaign_name ? (
            <p>
              <strong>Campaña:</strong> {client.campaign_name}
              {client.ad_name ? ` · ${client.ad_name}` : ""}
            </p>
          ) : null}
          {client.form_name ? (
            <p>
              <strong>Formulario:</strong> {client.form_name}
            </p>
          ) : null}
        </section>
      ) : null}

      <ClientNotesSection contactId={client.contact_id} />

      <ClientRequirements client={client} onChange={onSaved} />

      <section className="grid grid-cols-2 gap-3">
        <SimpleSelect
          label="Agente"
          value={draft.sales_id}
          onChange={set("sales_id")}
          options={workers.map((agent) => ({
            value: String(agent.id),
            label: agentName(agent),
          }))}
          placeholder="Sin coger"
        />
        <SimpleSelect
          label="Temperatura"
          value={draft.lead_temperature}
          onChange={set("lead_temperature")}
          options={LEAD_TEMPERATURES}
        />
        <SimpleSelect
          label="Franja para llamar"
          value={draft.call_slot}
          onChange={set("call_slot")}
          options={
            draft.call_slot?.includes(",")
              ? [
                  ...CALL_SLOTS,
                  {
                    value: draft.call_slot,
                    label: labelOf(CALL_SLOTS, draft.call_slot),
                  },
                ]
              : CALL_SLOTS
          }
        />
        <Field label="Recontactar el">
          <Input
            type="date"
            value={draft.recontact_at ?? ""}
            onChange={(event) => set("recontact_at")(event.target.value)}
          />
        </Field>
        <div className="col-span-2">
          <Field label="Dirección">
            <Input
              value={draft.address ?? ""}
              onChange={(event) => set("address")(event.target.value)}
            />
          </Field>
        </div>
        <Field label="Código postal">
          <Input
            value={draft.postal_code ?? ""}
            onChange={(event) => set("postal_code")(event.target.value)}
          />
        </Field>
        <Field label="Población">
          <Input
            value={draft.city ?? ""}
            onChange={(event) => set("city")(event.target.value)}
          />
        </Field>
        <Field label="Provincia">
          <Input
            value={draft.province ?? ""}
            onChange={(event) => set("province")(event.target.value)}
          />
        </Field>
        <SimpleSelect
          label="Instalador"
          value={draft.installer_id}
          onChange={set("installer_id")}
          options={installerOptions}
        />
        {client.stage === "no_purchase" ? (
          <div className="col-span-2">
            <SimpleSelect
              label="Motivo de no compra"
              value={draft.lost_reason}
              onChange={set("lost_reason")}
              options={LOST_REASONS}
            />
          </div>
        ) : null}
        <div className="col-span-2 flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </section>

      <ClientConversations
        contactId={client.contact_id}
        phone={client.phone}
        firstName={client.first_name}
      />

      <ClientTasks contactId={client.contact_id} name={fullName(client)} />

      <ClientPhotos contactId={client.contact_id} onChange={onSaved} />

      <section className="flex flex-col gap-2">
        <h4 className="text-sm font-semibold">Pedidos</h4>
        {orders.length ? (
          orders.map((order) => (
            <div key={order.id} className="rounded-md border p-3 text-sm">
              <div className="flex items-center justify-between">
                <strong>{labelOf(PRODUCTS, order.product)}</strong>
                <Badge variant="secondary">
                  {labelOf(ORDER_STAGES, order.stage)}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                {formatEuro(order.price)} ·{" "}
                {labelOf(PAYMENT_METHODS, order.payment_method)}
                {order.plan && order.plan !== "none"
                  ? ` · ${labelOf(PLANS, order.plan)}`
                  : ""}
                {order.paid_at ? " · Pagado" : " · Pendiente de pago"}
              </p>
              <CanAccess resource="money" action="list">
                <Link
                  to={`/dinero/beneficio?pedido=${order.id}`}
                  className="mt-1 inline-block text-xs text-muted-foreground hover:underline"
                >
                  Costes y beneficio de esta venta
                </Link>
              </CanAccess>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">
            Sin pedidos. Al moverlo a «Compra» se crea el pedido.
          </p>
        )}
      </section>

      <ClientWelcome contactId={client.contact_id} onChanged={onSaved} />

      <ClientStripe
        contactId={client.contact_id}
        stripeCustomerId={client.stripe_customer_id}
      />

      <Button variant="outline" asChild>
        <Link to={`/contacts/${client.contact_id}/show`}>
          <ExternalLink className="h-4 w-4" />
          Ficha completa: notas, tareas e historial
        </Link>
      </Button>
    </div>
  );
};
